import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
import { Maximize2, X } from 'lucide-react';
import type { WeddingMediaItem } from '@/src/types';
import { Reveal } from './shared';

type Props = {
  items: WeddingMediaItem[];
};

export function MediaShowcaseSection({ items }: Props) {
  const visibleItems = items.filter((item) => item.url?.trim());
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const activeItem = activeIndex === null ? null : visibleItems[activeIndex];

  useEffect(() => {
    if (!activeItem) return undefined;
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setActiveIndex(null);
    };
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.style.overflow = '';
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [activeItem]);

  if (visibleItems.length === 0) return null;

  return (
    <section className="bg-[#11130f] px-3 py-20 text-[#fffaf0] sm:px-5 lg:py-28">
      <Reveal className="px-2 pb-10 sm:px-5 lg:px-9">
        <p className="text-xs font-semibold uppercase tracking-[0.26em] text-[#d7b56d]">Memories</p>
        <h2 className="mt-4 text-[clamp(2.5rem,6.5vw,6.8rem)] font-semibold leading-[0.94] tracking-normal">
          A living gallery of the celebration.
        </h2>
      </Reveal>

      <div className="grid auto-rows-[34vh] grid-cols-2 gap-3 md:grid-cols-4 md:auto-rows-[40vh]">
        {visibleItems.slice(0, 8).map((item, index) => (
          <motion.figure
            key={`${item.url}-${index}`}
            initial={{ opacity: 0, scale: 0.96 }}
            whileInView={{ opacity: 1, scale: 1 }}
            whileHover={{ scale: 0.985 }}
            viewport={{ once: true, amount: 0.22 }}
            transition={{ duration: 0.68, delay: index * 0.035, ease: 'easeOut' }}
            className={`${index === 1 || index === 4 ? 'col-span-2' : ''} group relative overflow-hidden bg-[#211712]`}
          >
            <button
              type="button"
              onClick={() => setActiveIndex(index)}
              className="absolute inset-0 z-10 text-left focus:outline-none focus:ring-2 focus:ring-inset focus:ring-[#d7b56d]"
              aria-label="Open media fullscreen"
            >
              <span className="absolute right-3 top-3 grid h-10 w-10 place-items-center rounded-full bg-[#11130f]/68 text-[#fffaf0] opacity-0 backdrop-blur transition group-hover:opacity-100">
                <Maximize2 size={17} />
              </span>
            </button>
            {item.type === 'video' ? (
              <video src={item.url} className="h-full w-full object-cover" muted playsInline />
            ) : (
              <img src={item.url} alt="" className="h-full w-full object-cover transition duration-700 group-hover:scale-105" />
            )}
            {(item.caption || item.description) && (
              <figcaption className="absolute inset-x-0 bottom-0 translate-y-2 bg-gradient-to-t from-[#11130f]/86 to-transparent p-4 opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:opacity-100">
                {item.caption && <p className="text-base font-semibold">{item.caption}</p>}
                {item.description && <p className="mt-1 line-clamp-2 text-sm text-[#f7eee0]/72">{item.description}</p>}
              </figcaption>
            )}
          </motion.figure>
        ))}
      </div>
      <AnimatePresence>
        {activeItem && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center bg-[#050504]/94 p-4 text-[#fffaf0] sm:p-7"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <button
              type="button"
              onClick={() => setActiveIndex(null)}
              className="absolute right-4 top-4 z-10 grid h-11 w-11 place-items-center rounded-full bg-[#fffaf0] text-[#11130f] transition hover:bg-[#d7b56d] sm:right-7 sm:top-7"
              aria-label="Close fullscreen media"
            >
              <X size={20} />
            </button>
            <motion.figure
              initial={{ opacity: 0, scale: 0.96, y: 16 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.98, y: 8 }}
              transition={{ duration: 0.28, ease: 'easeOut' }}
              className="grid max-h-full w-full max-w-6xl gap-5"
            >
              <div className="max-h-[76vh] overflow-hidden bg-[#11130f]">
                {activeItem.type === 'video' ? (
                  <video src={activeItem.url} className="max-h-[76vh] w-full object-contain" controls autoPlay playsInline />
                ) : (
                  <img src={activeItem.url} alt="" className="max-h-[76vh] w-full object-contain" />
                )}
              </div>
              {(activeItem.caption || activeItem.description) && (
                <figcaption className="max-w-3xl">
                  {activeItem.caption && <p className="text-2xl font-semibold">{activeItem.caption}</p>}
                  {activeItem.description && <p className="mt-2 text-base leading-7 text-[#f7eee0]/72">{activeItem.description}</p>}
                </figcaption>
              )}
            </motion.figure>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
